import { addressToBytes32, Options } from "@layerzerolabs/lz-v2-utilities";
import { useContractView } from "logic/hooks";
import { applySlippage, ERC20Metadata, NetworkEids, OUSDaiUtilityAddresses, SendParam } from "logic/utils";
import { useMemo } from "react";
import { type Address, encodeAbiParameters, parseAbiParameters, toHex } from "viem";

import { OAdapterAbi } from "../../contracts";

import { DepositType } from "./useBuyTx";
import { useDepositParams } from "./useDepositParams";

interface useSrcChainSPParams {
  depositAmountUnits: bigint;
  depositToken: ERC20Metadata;
  depositMainToken: ERC20Metadata;
  depositType: DepositType;
  dstChainSP: SendParam;
  lzReceiveGasLimit: bigint;
  isBridging: boolean;
  enabled?: boolean;
}

export function useSrcChainSP(params: useSrcChainSPParams) {
  const { depositAmountUnits, depositToken, depositMainToken, depositType, dstChainSP, lzReceiveGasLimit, isBridging, enabled = true } =
    params;
  const { depositParamsData, depositParams } = useDepositParams({
    usdaiAmountUnits: depositAmountUnits,
    dstChainSP: dstChainSP,
  });

  const srcChainSP = useMemo(() => {
    const composeMsg = encodeAbiParameters(parseAbiParameters(`int24 msgType, bytes suffixData`), [
      depositType,
      depositParamsData,
    ]);

    const composeGasLimit = 1_000_000n;

    const srcChainSP: SendParam = {
      dstEid: NetworkEids[depositMainToken.chainId],
      to: toHex(addressToBytes32(OUSDaiUtilityAddresses[depositMainToken.chainId])),
      amountLD: depositAmountUnits,
      minAmountLD: applySlippage(depositAmountUnits, 1),
      extraOptions: Options.newOptions()
        .addExecutorLzReceiveOption(lzReceiveGasLimit)
        .addExecutorComposeOption(0, composeGasLimit, 0n)
        .toHex() as Address,
      composeMsg,
      oftCmd: "0x",
    };

    return srcChainSP;
  }, [depositType, depositParamsData, depositMainToken.chainId, depositAmountUnits, lzReceiveGasLimit]);

  const { data: srcChainSendQuote, error: srcChainSendQuoteError } = useContractView({
    abi: OAdapterAbi,
    address: depositToken.OftAdapter,
    functionName: "quoteSend",
    args: [{ ...srcChainSP }, false],
    chainId: depositToken.chainId,
    query: {
      enabled: Boolean(enabled && isBridging && depositAmountUnits),
    },
  });

  return {
    srcChainSP,
    depositParamsData,
    depositParams,
    srcChainSendQuote,
    srcChainSendQuoteError,
  };
}
